import subDays from "date-fns/subDays";
import {format} from "date-fns";
import {getApiDate} from "../../../../libs/common_utils";

export const campaign_statuses = (intl) => [
	{
		"text": intl.formatMessage({
			id: "STATUS_ALL",
			defaultMessage: "All",
		}),
		"value": "all",
	},
	{
		"text": intl.formatMessage({
			id: "STATUS_ACTIVE",
			defaultMessage: "Active",
		}),
		"value": "active",
	},
	{
		"text": intl.formatMessage({
			id: "STATUS_INACTIVE",
			defaultMessage: "Inactive",
		}),
		"value": "inactive",
	},
];

export const date_ranges = (intl) => [
	{
		"text": intl.formatMessage({
			id: "DATE_RANGE_YESTERDAY",
			defaultMessage: "Yesterday",
		}),
		"value": "yesterday",
	},
	{
		"text": intl.formatMessage({
			id: "DATE_RANGE_7DAYS",
			defaultMessage: "Last 7 days",
		}),
		"value": "7days",
	},
	{
		"text": intl.formatMessage({
			id: "DATE_RANGE_14DAYS",
			defaultMessage: "Last 14 days",
		}),
		"value": "14days",
	},
	{
		"text": intl.formatMessage({
			id: "DATE_RANGE_30DAYS",
			defaultMessage: "Last 30 days",
		}),
		"value": "30days",
	},
];

export const campaignViewOptions = (intl) => [
	{
		text: intl.formatMessage({
			id: "VALUE_PERFORMANCE",
			defaultMessage: "Performance",
		}),
		value: "performance",
	},
	{
		text: intl.formatMessage({
			id: "VALUE_SPEND",
			defaultMessage: "Spend",
		}),
		value: "spend",
	}
]

export const campaignDateWindows = (intl) => [
	{
		text: intl.formatMessage({
			id: "DATE_RANGE_TODAY",
			defaultMessage: "Today",
		}),
		value: "current_day"
	},
	{
		text: intl.formatMessage({
			id: "DATE_RANGE_YESTERDAY",
			defaultMessage: "Yesterday",
		}),
		value: "yesterday",
	},
	{
		text: intl.formatMessage({
			id: "DATE_RANGE_7DAYS",
			defaultMessage: "Last 7 days",
		}),
		value: "last_7_days",
	},
	{
		text: intl.formatMessage({
			id: "DATE_RANGE_14DAYS",
			defaultMessage: "Last 14 days",
		}),
		value: "last_14_days",
	},
	{
		text: intl.formatMessage({
			id: "DATE_RANGE_30DAYS",
			defaultMessage: "Last 30 days",
		}),
		value: "last_30_days",
	},
]

export default class Filter
{
	constructor() {
		this.reset();
	}

	/**
	 * update campaign status
	 * @param {string} value
	 */
	setStatus(value) {
		this.campaign_status = value.toLowerCase();
	}

	/**
	 * update reporting period
	 * @param {string} value
	 */
	setReportingPeriod(value) {
		this.reporting_period = value.toLowerCase();
	}

	/**
	 * update start_date
	 * @param {string} value
	 */
	setStartDate(value) {
		this.start_date = value;
	}

	/**
	 * update end_date
	 * @param {string} value
	 */
	setEndDate(value) {
		this.end_date = value;
	}

	/**
	 * update advertiser id
	 * @param {string} value
	 */
	setAdvertiser(value) {
		this.advertiser_id = parseInt(value, 10);
	}

	/**
	 * update campaign name
	 * @param {string} value
	 */
	setCampaignName(value) {
		this.campaign_name = value;
	}

	/**
	 * update date window
	 * @param {string} value
	 */
	setDateWindow(value) {
		this.time_window = value;
	}


	/**
	 * Set view by filter option
	 * @param {string} value - either "performance" or "spend"
	 */
	setViewBy(value) {
		this.view_by = value;
	}

	/**
	 * Checks if the current view is by performance
	 * @returns {boolean} true if view_by is "performance", false otherwise
	 */
	isPerformanceView() {
		return this.view_by === "performance";
	}

	/**
	 * unpack item from json format
	 * @param {object} item
	 */
	fromJson(item) {
		this.campaign_name = item.campaign_name;
		this.campaign_status = item.campaign_status;
		this.advertiser_id = item.advertiser_id;
		this.reporting_period = item.reporting_period;
		this.start_date = item.start_date;
		this.end_date = item.end_date;
		this.time_window = item.time_window;
		this.view_by = item.view_by;
	}

	/**
	 * reset filter data
	 */
	reset() {
		this.campaign_status = "all";
		this.campaign_name = "";
		this.advertiser_id = 0;
		this.reporting_period = "7days";
		this.start_date = subDays(new Date(), 7);
		this.end_date = new Date();
		this.time_window = "last_7_days";
		this.view_by = "performance";
	}

	/**
	 * Transform data into API object
	 * @returns {object}
	 */
	toJson() {
		return {
			"campaign_name": this.campaign_name,
			"campaign_status": this.campaign_status,
			"advertiser_id": this.advertiser_id,
			"reporting_period": this.reporting_period,
			"start_date": (this.start_date instanceof Date)? getApiDate(this.start_date) : this.start_date ?? undefined,
			"end_date": (this.end_date instanceof Date)? getApiDate(this.end_date) : this.end_date ?? undefined,
			"time_window": this.time_window,
			"view_by": this.view_by,
		}
	}

	/**
	 * transform data for graph api call
	 * @returns {{end_date: string, start_date: string}}
	 */
	toGraphJson() {
		if (!this.hasStartEndDate()) {
			const startDate = subDays(new Date(), 7);
			const endDate = new Date();
			return {
				"start_date": getApiDate(startDate),
				"end_date": getApiDate(endDate),
			};
		}
		return {
			"start_date": (this.start_date instanceof Date)? getApiDate(this.start_date) : this.start_date,
			"end_date": (this.end_date instanceof Date)? getApiDate(this.end_date) : this.end_date,
		}
	}

	/**
	 * Check if the filter has start and end dates set.
	 * @return {boolean} true if both start and end dates are set, false otherwise.
	 */
	hasStartEndDate() {
		return Boolean(this.start_date && this.end_date);
	}

	/**
	 * Resets the start and end dates of the filter to be the last 7 days from today.
	 */
	resetStartEndDate() {
		this.start_date = subDays(new Date(), 7);
		this.end_date = new Date();
	}

	/**
	 * transform data into request json
	 * @returns {object}
	 */
	toRequestJson() {
		const performanceMetrics ={
			"start_date": (this.start_date instanceof Date)? getApiDate(this.start_date) : this.start_date ?? getApiDate(subDays(new Date(), 7)),
			"end_date": (this.end_date instanceof Date)? getApiDate(this.end_date) : this.end_date ?? getApiDate(new Date()),
		}
		const spendMetrics = {
			"time_window": this.time_window,
		}
		let r = {
			view_by: this.view_by
		};
		if(this.campaign_status !== "all") {
			r["campaign_status"] = this.campaign_status;
		}

		if(this.advertiser_id > 0) {
			r["advertiser_id"] = this.advertiser_id;
		}

		if(this.campaign_name !== "") {
			r["campaign_name"] = this.campaign_name;
		}

		if (this.isPerformanceView()) {
			if (!this.hasStartEndDate()) {
				this.setStartDate(performanceMetrics.start_date);
				this.setEndDate(performanceMetrics.end_date);
			}
			r = { ...r, ...performanceMetrics }
		} else {
			r = { ...r, ...spendMetrics }
		}

		return Object.assign(r, this.hasStartEndDate() ? this.toGraphJson() : {});
	}

	/**
	 * check that user didn't touch filter
	 * @return {boolean}
	 */
	isEmpty() {
		return this.campaign_status === "all" && this.advertiser_id === 0 && this.campaign_name === "";
	}

	/**
	 * Build URLSearchParams from the current filter
	 * @returns {string} like '?campaign_status=all&advertiser_id=0&campaign_name=...'
	 */
	toQueryString() {
		const p = new URLSearchParams();
		p.set("campaign_status", this.campaign_status || "all");
		p.set("advertiser_id", String(this.advertiser_id || 0));
		p.set("campaign_name", this.campaign_name || "");
		if (this.isPerformanceView()) {
			// keep both explicit dates (if any) and the time window
			if (this.start_date) p.set("start_date", (this.start_date instanceof Date) ? getApiDate(this.start_date) : this.start_date);
			if (this.end_date)   p.set("end_date",   (this.end_date   instanceof Date) ? getApiDate(this.end_date)   : this.end_date);
		} else {
			p.set("time_window", this.time_window || "last_7_days");
		}
		p.set("view_by", this.view_by || "performance");
		return `?${p.toString()}`;
	}

	/**
	 * Hydrate the filter from URLSearchParams
	 * @param {URLSearchParams} params
	 */
	fromQueryParams(params) {
		if (!params) return;

		const get = (k, d = null) => params.get(k) ?? d;

		this.campaign_status = (get("campaign_status", "all") || "all").toLowerCase();
		this.advertiser_id   = parseInt(get("advertiser_id", "0"), 10) || 0;
		this.campaign_name   = get("campaign_name", "") || "";

		const s = get("start_date", getApiDate(subDays(new Date(), 7)));
		const e = get("end_date", getApiDate(new Date()));
		this.start_date = s;
		this.end_date   = e;

		this.time_window = get("time_window", "last_7_days") || "last_7_days";
		this.view_by = get("view_by", "performance") || "performance";
	}
}
